import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  WbSunny,
  Thermostat,
  Air,
  Cloud,
  CloudQueue,
  Thunderstorm,
  AcUnit,
  Grain,
  Visibility,
  Error as ErrorIcon
} from '@mui/icons-material';
import { makeApiCall } from '../config/api';

const WeatherWidget = ({ location = 'New York,US', lat = null, lon = null }) => {
  const [weatherData, setWeatherData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Function to get appropriate weather icon based on condition
  const getWeatherIcon = (condition, iconCode) => {
    const iconProps = { fontSize: '1.2rem' };
    
    // Use icon code for more specific icons
    if (iconCode) {
      if (iconCode.includes('01')) return <WbSunny sx={{ ...iconProps, color: '#de6127' }} />; // Clear
      if (iconCode.includes('02')) return <CloudQueue sx={{ ...iconProps, color: '#757575' }} />; // Few clouds
      if (iconCode.includes('03') || iconCode.includes('04')) return <Cloud sx={{ ...iconProps, color: '#757575' }} />; // Clouds
      if (iconCode.includes('09') || iconCode.includes('10')) return <Grain sx={{ ...iconProps, color: '#1976d2' }} />; // Rain
      if (iconCode.includes('11')) return <Thunderstorm sx={{ ...iconProps, color: '#7b1fa2' }} />; // Thunderstorm
      if (iconCode.includes('13')) return <AcUnit sx={{ ...iconProps, color: '#00bcd4' }} />; // Snow
      if (iconCode.includes('50')) return <Visibility sx={{ ...iconProps, color: '#9e9e9e' }} />; // Mist/Fog
    }
    
    // Fallback to condition-based icons
    switch (condition?.toLowerCase()) {
      case 'clear':
        return <WbSunny sx={{ ...iconProps, color: '#de6127' }} />;
      case 'clouds':
        return <Cloud sx={{ ...iconProps, color: '#757575' }} />;
      case 'rain':
      case 'drizzle':
        return <Grain sx={{ ...iconProps, color: '#1976d2' }} />;
      case 'thunderstorm':
        return <Thunderstorm sx={{ ...iconProps, color: '#7b1fa2' }} />;
      case 'snow':
        return <AcUnit sx={{ ...iconProps, color: '#00bcd4' }} />;
      case 'mist':
      case 'fog':
      case 'haze':
        return <Visibility sx={{ ...iconProps, color: '#9e9e9e' }} />;
      default:
        return <WbSunny sx={{ ...iconProps, color: '#de6127' }} />;
    }
  };

  // Function to get wind direction
  const getWindDirection = (degrees) => {
    if (degrees >= 337.5 || degrees < 22.5) return 'N';
    if (degrees >= 22.5 && degrees < 67.5) return 'NE';
    if (degrees >= 67.5 && degrees < 112.5) return 'E';
    if (degrees >= 112.5 && degrees < 157.5) return 'SE';
    if (degrees >= 157.5 && degrees < 202.5) return 'S';
    if (degrees >= 202.5 && degrees < 247.5) return 'SW';
    if (degrees >= 247.5 && degrees < 292.5) return 'W';
    if (degrees >= 292.5 && degrees < 337.5) return 'NW';
    return 'N';
  };

  // Function to convert Fahrenheit to Celsius
  const fahrenheitToCelsius = (fahrenheit) => {
    return Math.round((fahrenheit - 32) * 5 / 9);
  };

  // Function to convert mph to km/h
  const mphToKmh = (mph) => {
    return Math.round(mph * 1.60934);
  };

  // Fetch weather data
  const fetchWeatherData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams();
      
      if (lat && lon) {
        params.append('lat', lat);
        params.append('lon', lon);
      } else if (location) {
        params.append('location', location);
      }
      
      const endpoint = params.toString() 
        ? `api/weather/current?${params.toString()}`
        : 'api/weather/current';
      
      const response = await makeApiCall(endpoint);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.error);
      }
      
      setWeatherData(data);
      
    } catch (err) {
      console.error('Error fetching weather data:', err);
      setError(err.message || 'Failed to fetch weather data');
    } finally {
      setLoading(false);
    }
  }, [location, lat, lon]);

  // Initial load and refresh every 10 minutes
  useEffect(() => {
    fetchWeatherData();
    
    const interval = setInterval(fetchWeatherData, 10 * 60 * 1000); // 10 minutes
    
    return () => clearInterval(interval);
  }, [fetchWeatherData]);

  // Loading state
  if (loading && !weatherData) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2 }}>
        <CircularProgress size={16} />
        <Typography variant="body2" sx={{ color: '#000062' }}>
          Loading weather...
        </Typography>
      </Box>
    );
  }

  // Error state
  if (error && !weatherData) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2 }}>
        <ErrorIcon sx={{ color: '#d32f2f', fontSize: '1.2rem' }} />
        <Typography variant="body2" sx={{ color: '#d32f2f' }}>
          Weather unavailable
        </Typography>
      </Box>
    );
  }

  // No data state
  if (!weatherData) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2 }}>
        <Typography variant="body2" sx={{ color: '#757575' }}>
          No weather data
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mr: 2 }}>
      {/* Weather Condition */}
      <Tooltip title={`${weatherData.description} in ${weatherData.location}`}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {getWeatherIcon(weatherData.condition, weatherData.icon)}
          <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#000062' }}>
            {weatherData.condition}
          </Typography>
        </Box>
      </Tooltip>

      {/* Temperature */}
      <Tooltip title={`Feels like ${weatherData.feelsLike}°F (${fahrenheitToCelsius(weatherData.feelsLike)}°C)`}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Thermostat sx={{ color: '#000062', fontSize: '1.2rem' }} />
          <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#000062' }}>
            {weatherData.temperature}°F / {fahrenheitToCelsius(weatherData.temperature)}°C
          </Typography>
        </Box>
      </Tooltip>

      {/* Wind Speed - Hidden on mobile */}
      <Tooltip title={`Wind: ${mphToKmh(weatherData.windSpeed)} km/h ${getWindDirection(weatherData.windDirection)} • Auto-refreshes every 10 minutes`}>
        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5 }}>
          <Air sx={{ color: '#000062', fontSize: '1.2rem' }} />
          <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#000062' }}>
            {mphToKmh(weatherData.windSpeed)} km/h
          </Typography>
        </Box>
      </Tooltip>
    </Box>
  );
};

export default WeatherWidget; 